import { Accordion, Button, Progress, Switch } from '@eleveo/components-react';
import { BellIcon, BellOffIcon, PlusIcon } from '@eleveo/icons-react';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IntradayNotificationToDuplicate,
  IntradayQueuesToOptions,
  NotificationForm,
  NotificationFormType,
} from './NotificationForm';
import { NotificationTile } from './NotificationTile';
import { ADD_NEW_NOTIFICATION_BUTTON_TAG } from './constants';
import { usePushNotifications } from '../../app/push/PushNotificationProvider';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { ModalConfirmationNewStyle } from '../../components/ModalConfirmationNewStyle';
import {
  CreateIntradayNotificationMutationVariables,
  GetIntradayPushNotificationOnOffSettingDocument,
  IntradayQueueNotification,
  UpdateIntradayNotificationMutationVariables,
  useCreateIntradayNotificationMutation,
  useDeleteIntradayNotificationMutation,
  useGetIntradayNotificationsQuery,
  useUpdateIntradayNotificationMutation,
  useUpdateIntradayPushNotificationOnOffSettingMutation,
} from '../../graphql/components';
import { recordBy } from '../../utils/array';

type IntradayNotificationsSidebarProps = {
  handleCancel: () => void;
  hasEditPermission: boolean;
  queuesList: IntradayQueuesToOptions;
};

export const IntradayNotificationsSidebar: React.FC<IntradayNotificationsSidebarProps> = ({
  handleCancel,
  hasEditPermission,
  queuesList,
}) => {
  const { t } = useTranslation();

  const [isCreateNotificationActive, setIsCreateNotificationActive] = useState(false);
  const [notificationToDuplicate, setNotificationToDuplicate] = useState<IntradayNotificationToDuplicate | undefined>(
    undefined
  );
  const [showDisableNotificationsModal, setShowDisableNotificationsModal] = useState(false);
  const pushNotifications = usePushNotifications();
  const pushNotificationEnabled = pushNotifications.notifications.intraday;

  const {
    data: intradayNotificationsData,
    loading: intradayNotificationsLoading,
    error: intradayNotificationsError,
    refetch: intradayNotificationsRefetch,
  } = useGetIntradayNotificationsQuery({
    fetchPolicy: 'network-only',
  });

  // Mutations
  const [createNotification, { loading: createNotificationLoading }] = useCreateIntradayNotificationMutation();
  const [editNotification, { loading: editNotificationLoading }] = useUpdateIntradayNotificationMutation();
  const [deleteNotification, { loading: deleteNotificationLoading }] = useDeleteIntradayNotificationMutation();

  const [editPushNotificationSetting, { loading: editPushNotificationSettingLoading }] =
    useUpdateIntradayPushNotificationOnOffSettingMutation();

  const intradayNotifications: IntradayQueueNotification[] = useMemo(
    () => intradayNotificationsData?.intradayNotifications ?? [],
    [intradayNotificationsData?.intradayNotifications]
  );
  const intradayNotificationsObj = useMemo(() => recordBy(intradayNotifications, 'queueId'), [intradayNotifications]);

  const notUsedQueueIds = useMemo(
    () => queuesList.filter(({ id }) => !intradayNotificationsObj[id]),
    [queuesList, intradayNotificationsObj]
  );

  const loading =
    intradayNotificationsLoading ||
    createNotificationLoading ||
    editNotificationLoading ||
    deleteNotificationLoading ||
    editPushNotificationSettingLoading;
  const error = intradayNotificationsError;

  console.log(error); // TODO: Add error scenario message in next iteration of Intraday

  const memoizedIntradayNotificationsToAccordionItems = useMemo(
    () =>
      intradayNotifications.map(({ queueId }) => {
        const queue = queuesList.find(({ id }) => id === queueId);

        return {
          id: queueId,
          title: queue?.textValue || '',
          icon: pushNotificationEnabled ? <BellIcon /> : <BellOffIcon />,
          iconColor: 'info' as const,
        };
      }),
    [intradayNotifications, pushNotificationEnabled, queuesList]
  );

  const newNotificationToAccordionItem = [
    {
      id: 'NewNotificationId',
      title: t('intraday.newNotification'),
      icon: <BellIcon />,
      iconColor: 'info' as const,
    },
  ];

  const handleDelete = async (queueId: string) => {
    try {
      await deleteNotification({
        variables: {
          queueId,
        },
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      intradayNotificationsRefetch();
    }
  };

  const handleUpdate = (callBack: () => void) => async (values: NotificationFormType) => {
    const { queueId, slaOutOfRangeFrom, slaOutOfRangeTo, contactVolumeDeviation } = values;

    const variables: UpdateIntradayNotificationMutationVariables = {
      intradayQueueNotification: { queueId, slaOutOfRangeFrom, slaOutOfRangeTo, contactVolumeDeviation },
    };

    try {
      await editNotification({
        variables,
      });

      callBack();
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      intradayNotificationsRefetch();
    }
  };

  const handleCreate = async (values: NotificationFormType) => {
    const { queueId, slaOutOfRangeFrom, slaOutOfRangeTo, contactVolumeDeviation } = values;

    const variables: CreateIntradayNotificationMutationVariables = {
      intradayQueueNotification: {
        queueId: queueId.toString(), // Needed as Select component is returning ['queueId'] array
        contactVolumeDeviation,
        slaOutOfRangeFrom,
        slaOutOfRangeTo,
      },
    };

    try {
      await createNotification({
        variables,
      });

      setIsCreateNotificationActive(false);
      intradayNotificationsRefetch();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const openCreateForm = () => {
    setNotificationToDuplicate(undefined);

    if (!isCreateNotificationActive) {
      setIsCreateNotificationActive(true);
    }
  };

  const handleDuplicate = ({
    contactVolumeDeviation,
    slaOutOfRangeFrom,
    slaOutOfRangeTo,
  }: IntradayQueueNotification) => {
    setIsCreateNotificationActive(true);
    setNotificationToDuplicate({
      contactVolumeDeviation: contactVolumeDeviation ?? NaN,
      slaOutOfRangeFrom: slaOutOfRangeFrom ?? NaN,
      slaOutOfRangeTo: slaOutOfRangeTo ?? NaN,
    });
  };

  const editPushNotification = async (value: boolean) => {
    try {
      await editPushNotificationSetting({
        variables: {
          intradayPushNotificationSetting: {
            enabled: value,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [GetIntradayPushNotificationOnOffSettingDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setShowDisableNotificationsModal(false);
    }
  };

  const handlePushNotificationChange = async (value: boolean) => {
    if (value === false) {
      setShowDisableNotificationsModal(true);
    } else {
      editPushNotification(value);
    }
  };

  return (
    <Sidebar onClose={handleCancel} title={t('intraday.notifications')}>
      {loading && <Progress withOverlay />}

      <SidebarPanel>
        <div className={clsx('flex', { 'justify-end': !hasEditPermission, 'justify-between': hasEditPermission })}>
          {hasEditPermission && (
            <Button testId={ADD_NEW_NOTIFICATION_BUTTON_TAG} variant="outline" onClick={openCreateForm}>
              <PlusIcon />
              {t('form.addNew')}
            </Button>
          )}
          <Switch
            isDisabled={!hasEditPermission}
            isSelected={pushNotificationEnabled}
            onChange={handlePushNotificationChange}
          >
            {t('notification.pushNotifications')}
          </Switch>
        </div>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          {/* Accordion for creating NEW Intraday Queue Notification */}
          {isCreateNotificationActive && (
            <Accordion
              expandedKeys={[newNotificationToAccordionItem[0].id]}
              items={newNotificationToAccordionItem}
              expansionMode="single"
            >
              {({ id }) => (
                <NotificationForm
                  handleCancel={() => setIsCreateNotificationActive(false)}
                  intradayQueueNotification={notificationToDuplicate}
                  isCreate
                  key={id}
                  onSubmit={handleCreate}
                  queuesList={notUsedQueueIds}
                />
              )}
            </Accordion>
          )}

          {/* Accordion for existing Intraday Queue Notifications */}
          <Accordion items={memoizedIntradayNotificationsToAccordionItems} expansionMode="multiple">
            {({ id, title }) => (
              <NotificationTile
                handleDelete={handleDelete}
                handleDuplicate={handleDuplicate}
                hasEditPermission={hasEditPermission}
                intradayQueueNotification={intradayNotificationsObj[id]}
                key={id}
                onSubmit={handleUpdate}
                queuesList={queuesList}
                title={title}
              />
            )}
          </Accordion>
        </SidebarPart>
      </SidebarContent>

      <ModalConfirmationNewStyle
        acceptCaption={t('layout.action.confirm')}
        cancelCaption={t('layout.action.cancel')}
        isOpen={showDisableNotificationsModal}
        onAccept={() => editPushNotification(false)}
        onCancel={() => setShowDisableNotificationsModal(false)}
        title={t('layout.confirmationRequired')}
      >
        <p>{t('intraday.pushNotificationsModal.body')}</p>
        <p>{t('notification.modalBodyDisableSteps')}</p>
      </ModalConfirmationNewStyle>
    </Sidebar>
  );
};
